import React, { useEffect, useState } from 'react';
import Header from "../Header";
import Sidebar from "./SideBar";
import Content from '../Content';
import authStorage from '../../utilities/Storage';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  FiUsers, FiSearch, FiFilter, FiDownload, FiEdit, FiTrash2, 
  FiEye, FiPlus, FiRefreshCw, FiChevronLeft, FiChevronRight,
  FiUserPlus, FiMail, FiCalendar, FiCheckCircle, FiXCircle, FiAlertCircle, FiTrendingUp
} from 'react-icons/fi';

export default function MembersPage() {
  const [email, setEmail] = useState(null);
  const [company_name, setCompany_Name] = useState(null);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'active', 'inactive', 'pending'
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [error, setError] = useState(null);

  // API base URL
  const API_URL = 'https://backend-livestream-production.up.railway.app/api';

  // Fetch members data from your API - using the same pattern as CommunityChat
  const fetchMembers = async () => {
    try {
      setRefreshing(true);
      setError(null);
      
      // Get user email from storage
      const userData = authStorage.getUser();
      let userEmail = null;
      
      if (userData) {
        try {
          const user = JSON.parse(userData);
          userEmail = user.email;
        } catch (error) {
          console.error("Failed to parse user data:", error);
        }
      }
      
      if (!userEmail) {
        throw new Error('User email not found. Please log in again.');
      }
      
      // Call your API to get users - using GET as per your route definition
      const response = await axios.get(`${API_URL}/get-userbyEmail`, {
        params: { email: userEmail }
      });
      
      // Handle the response exactly like CommunityChat does
      if (response.data && Array.isArray(response.data.data)) {
        const formattedMembers = response.data.data.map(user => {
          // Parse b2b_interest if it's a string
          let b2bInterests = [];
          if (user.b2b_interest) {
            if (typeof user.b2b_interest === 'string') {
              try {
                b2bInterests = JSON.parse(user.b2b_interest);
              } catch (e) {
                // If parsing fails, treat as a single interest
                b2bInterests = [user.b2b_interest];
              }
            } else if (Array.isArray(user.b2b_interest)) {
              b2bInterests = user.b2b_interest;
            }
          }
          
          return {
            id: user._id || user.id,
            name: user.full_name || user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown',
            email: user.email || 'No email',
            role: user.position || 'Member',
            status: user.status || 'active',
            organization: user.organization || user.company_name || 'No organization',
            phone: user.phone || 'No phone',
            country: user.country || 'No country',
            joinDate: user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown',
            lastActive: user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Unknown',
            // Store all the original data for detailed view
            fullData: user,
            // Additional fields from your data structure
            title: user.title || '',
            participationType: user.participation_type || '',
            exhibitBooth: user.exhibit_booth || '',
            b2bDetails: user.b2b_details || '',
            companyProfile: user.company_profile || '',
            companySector: user.company_sector || '',
            productsServices: user.products_services || '',
            companyEmail: user.company_email || '',
            companyAddress: user.company_address || '',
            companyPhone: user.company_phone || '',
            companyWebsite: user.company_website || '',
            socialMedia: user.social_media || '',
            sponsorshipInterest: user.sponsorship_interest || false,
            needInvoice: user.need_invoice || false,
            comments: user.comments || '',
            b2bInterests: b2bInterests
          };
        });
        
        setMembers(formattedMembers);
        setTotalPages(Math.ceil(formattedMembers.length / 10));
      } else {
        throw new Error('Invalid response structure from API');
      }
    } catch (error) {
      console.error('Error fetching members:', error);
      setError(error.message || 'Failed to fetch members');
      setMembers([]); // Set empty array to prevent map errors
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Update member status
  const updateMemberStatus = async (memberId, newStatus) => {
    try {
      setError(null);
      
      // Find the member to get their email
      const member = members.find(m => m.id === memberId);
      if (!member) {
        throw new Error('Member not found');
      }
      
      // Call your API to update user status
      const response = await axios.patch(`${API_URL}/update-user`, 
        { email: member.email, status: newStatus }
      );
      
      if (response && response.data && response.data.success) {
        // Update local state
        setMembers(members.map(m => 
          m.id === memberId ? { ...m, status: newStatus } : m
        ));
      } else {
        throw new Error(response.data?.message || 'Failed to update member status');
      }
    } catch (error) {
      console.error('Error updating member status:', error);
      setError(error.message || 'Failed to update member status');
    }
  };

  function getUserRegistrationDetails() {
    const userData = authStorage.getUser();
    if (!userData) return;
    
    try {
      const user = JSON.parse(userData);
      setEmail(user.email);
      setCompany_Name(user.company_name);
    } catch (error) {
      console.error("Failed to parse user data:", error);
      setLoading(false);
    }
  }

  useEffect(() => {
    getUserRegistrationDetails();
    fetchMembers();
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleRefresh = () => {
    fetchMembers();
  };

  const handleViewMember = (member) => {
    setSelectedMember(member);
    setShowViewModal(true);
  };

  const handleEditMember = (member) => {
    setSelectedMember(member);
    // Navigate to edit page or open edit modal
    console.log('Edit member:', member);
  };

  const handleDeleteMember = async (memberId) => {
    // Show confirmation dialog and delete if confirmed
    if (window.confirm('Are you sure you want to delete this member?')) {
      try {
        setError(null);
        
        // Find the member to get their email
        const member = members.find(m => m.id === memberId);
        if (!member) {
          throw new Error('Member not found');
        }
        
        // Update local state for now (assuming no delete endpoint)
        setMembers(members.filter(member => member.id !== memberId));
      } catch (error) {
        console.error('Error deleting member:', error);
        setError(error.message || 'Failed to delete member');
      }
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="badge bg-success bg-opacity-10 text-success">Active</span>;
      case 'inactive':
        return <span className="badge bg-secondary bg-opacity-10 text-secondary">Inactive</span>;
      case 'pending':
        return <span className="badge bg-warning bg-opacity-10 text-warning">Pending</span>;
      default:
        return <span className="badge bg-secondary bg-opacity-10 text-secondary">Unknown</span>;
    }
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  // Filter members based on search term and status
  const filteredMembers = members.filter(member => {
    const matchesSearch = member.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.organization.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || member.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Pagination
  const indexOfLastMember = currentPage * 10;
  const indexOfFirstMember = indexOfLastMember - 10;
  const currentMembers = filteredMembers.slice(indexOfFirstMember, indexOfLastMember);

  return (
    <div className="app">
      {/* Mobile Header */}
      <div className="d-md-none mobile-header bg-white shadow-sm p-3 d-flex justify-content-between align-items-center">
        <button 
          className="btn btn-outline-secondary"
          onClick={toggleMobileMenu}
        >
          <i className="bi bi-list"></i>
        </button>
        <h5 className="mb-0 fw-bold">Members</h5>
        <button className="btn btn-outline-secondary" onClick={handleRefresh}>
          {refreshing ? (
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          ) : (
            <FiRefreshCw />
          )}
        </button>
      </div>

      {/* Sidebar with mobile toggle */}
      <div className={`sidebar-wrapper ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <Sidebar />
      </div>

      {/* Overlay for mobile menu */}
      {mobileMenuOpen && (
        <div 
          className="mobile-menu-overlay"
          onClick={toggleMobileMenu}
        ></div>
      )}

      <div className="main-content">
        {/* Regular Header (hidden on mobile) */}
        <div className="d-none d-md-block">
          <Header />
        </div>

        <Content>
          <motion.div 
            className="container-fluid py-4"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            {/* Page Header */}
            <motion.div variants={itemVariants} className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h2 className="fw-bold mb-1">Members</h2>
                  <p className="text-muted mb-0">Manage your team members and their permissions</p>
                </div>
                <div className="d-flex gap-2">
                  {/* <button className="btn btn-primary" onClick={() => setShowAddMemberModal(true)}>
                    <FiUserPlus className="me-2" />Add Member
                  </button> */}
                  <button className="btn btn-outline-primary" onClick={handleRefresh}>
                    {refreshing ? (
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    ) : (
                      <FiRefreshCw />
                    )}
                  </button>
                </div>
              </div>
              
              {/* Error Alert */}
              {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  {error}
                  <button type="button" className="btn-close" onClick={() => setError(null)}></button>
                </div>
              )}
            </motion.div>

            {/* Stats Cards */}
            <motion.div variants={itemVariants} className="row mb-4">
              <div className="col-xl-3 col-md-6 mb-4">
                <div className="card stats-card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="stats-icon stats-icon-primary me-3">
                        <FiUsers />
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="text-muted mb-1">Total Members</h6>
                        <h3 className="fw-bold mb-0">{members.length}</h3>
                      </div>
                    </div>
                    <div className="mt-3 d-flex align-items-center">
                      <span className="badge bg-success bg-opacity-10 text-success">
                        <FiTrendingUp className="me-1" />12%
                      </span>
                      <span className="text-muted ms-2 small">vs last month</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-xl-3 col-md-6 mb-4">
                <div className="card stats-card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="stats-icon stats-icon-success me-3">
                        <FiCheckCircle />
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="text-muted mb-1">Active Members</h6>
                        <h3 className="fw-bold mb-0">{members.filter(m => m.status === 'active').length}</h3>
                      </div>
                    </div>
                    <div className="mt-3 d-flex align-items-center">
                      <span className="badge bg-success bg-opacity-10 text-success">
                        <FiTrendingUp className="me-1" />8%
                      </span>
                      <span className="text-muted ms-2 small">vs last month</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-xl-3 col-md-6 mb-4">
                <div className="card stats-card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="stats-icon stats-icon-warning me-3">
                        <FiAlertCircle />
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="text-muted mb-1">Pending</h6>
                        <h3 className="fw-bold mb-0">{members.filter(m => m.status === 'pending').length}</h3>
                      </div>
                    </div>
                    <div className="mt-3 d-flex align-items-center">
                      <span className="badge bg-warning bg-opacity-10 text-warning">
                        <FiAlertCircle className="me-1" />Awaiting
                      </span>
                      <span className="text-muted ms-2 small">approval</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-xl-3 col-md-6 mb-4">
                <div className="card stats-card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="stats-icon stats-icon-danger me-3">
                        <FiXCircle />
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="text-muted mb-1">Inactive</h6>
                        <h3 className="fw-bold mb-0">{members.filter(m => m.status === 'inactive').length}</h3>
                      </div>
                    </div>
                    <div className="mt-3 d-flex align-items-center">
                      <span className="badge bg-danger bg-opacity-10 text-danger">
                        <FiAlertCircle className="me-1" />5%
                      </span>
                      <span className="text-muted ms-2 small">of total</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Search and Filter */}
            <motion.div variants={itemVariants} className="row mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div className="input-group">
                          <span className="input-group-text bg-white border-end-0">
                            <FiSearch />
                          </span>
                          <input 
                            type="text" 
                            className="form-control border-start-0" 
                            placeholder="Search by name, email or organization..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-md-3">
                        <select 
                          className="form-select" 
                          value={filterStatus}
                          onChange={(e) => setFilterStatus(e.target.value)}
                        >
                          <option value="all">All Status</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                          <option value="pending">Pending</option>
                        </select>
                      </div>
                      <div className="col-md-3">
                        <button className="btn btn-outline-secondary w-100">
                          <FiFilter className="me-2" />More Filters
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Members Table */}
            <motion.div variants={itemVariants} className="row">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 fw-bold">All Members</h5>
                    <button className="btn btn-sm btn-outline-secondary">
                      <FiDownload className="me-1" />Export
                    </button>
                  </div>
                  <div className="card-body p-0">
                    {loading ? (
                      <div className="d-flex justify-content-center align-items-center p-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    ) : members.length === 0 ? (
                      <div className="text-center p-5">
                        <p className="text-muted">No members found</p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-hover mb-0">
                          <thead className="table-light">
                            <tr>
                              <th scope="col">Member</th>
                              <th scope="col">Organization</th>
                              <th scope="col">Role</th>
                              <th scope="col">Status</th>
                              <th scope="col">Join Date</th>
                              <th scope="col" className="text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentMembers.map((member) => (
                              <tr key={member.id}>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <div className="user-avatar bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                                      {getInitials(member.name)}
                                    </div>
                                    <div>
                                      <h6 className="mb-0 fw-medium">{member.name}</h6>
                                      <small className="text-muted">{member.email}</small>
                                    </div>
                                  </div>
                                </td>
                                <td>{member.organization}</td>
                                <td>{member.role}</td>
                                <td>{getStatusBadge(member.status)}</td>
                                <td>{member.joinDate}</td>
                                <td className="text-end">
                                  <div className="btn-group">
                                    <button 
                                      className="btn btn-sm btn-outline-secondary" 
                                      onClick={() => handleViewMember(member)}
                                    >
                                      <FiEye />
                                    </button>
                                    <button 
                                      className="btn btn-sm btn-outline-secondary" 
                                      onClick={() => handleEditMember(member)}
                                    >
                                      <FiEdit />
                                    </button>
                                    {member.status === 'pending' && (
                                      <button 
                                        className="btn btn-sm btn-outline-success" 
                                        onClick={() => updateMemberStatus(member.id, 'active')}
                                        title="Approve"
                                      >
                                        <FiCheckCircle />
                                      </button>
                                    )}
                                    {member.status === 'active' && (
                                      <button 
                                        className="btn btn-sm btn-outline-warning" 
                                        onClick={() => updateMemberStatus(member.id, 'inactive')}
                                        title="Deactivate"
                                      >
                                        <FiXCircle />
                                      </button>
                                    )}
                                    <button 
                                      className="btn btn-sm btn-outline-danger" 
                                      onClick={() => handleDeleteMember(member.id)}
                                    >
                                      <FiTrash2 />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                  <div className="card-footer bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                    <div className="text-muted small">
                      Showing {indexOfFirstMember + 1} to {Math.min(indexOfLastMember, filteredMembers.length)} of {filteredMembers.length} members
                    </div>
                    <div className="d-flex gap-1">
                      <button 
                        className="btn btn-sm btn-outline-secondary" 
                        onClick={() => setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)}
                        disabled={currentPage === 1}
                      >
                        <FiChevronLeft />
                      </button>
                      {[...Array(totalPages)].map((_, i) => (
                        <button 
                          key={i} 
                          className={`btn btn-sm ${currentPage === i + 1 ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => setCurrentPage(i + 1)}
                        >
                          {i + 1}
                        </button>
                      ))}
                      <button 
                        className="btn btn-sm btn-outline-secondary" 
                        onClick={() => setCurrentPage(currentPage < totalPages ? currentPage + 1 : totalPages)}
                        disabled={currentPage === totalPages}
                      >
                        <FiChevronRight />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </Content>
      </div>

      {/* View Member Modal */}
      {showViewModal && selectedMember && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Member Details</h5>
                <button type="button" className="btn-close" onClick={() => setShowViewModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="d-flex align-items-center mb-4">
                  <div className="user-avatar bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '60px', height: '60px', fontSize: '1.5rem' }}>
                    {getInitials(selectedMember.name)}
                  </div>
                  <div>
                    <h4 className="mb-1">{selectedMember.name}</h4>
                    <p className="text-muted mb-0">{selectedMember.email}</p>
                  </div>
                </div>
                
                <div className="row mb-3">
                  <div className="col-md-6 mb-3">
                    <p className="text-muted small mb-1">Organization</p>
                    <p className="mb-0 fw-medium">{selectedMember.organization}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <p className="text-muted small mb-1">Role</p>
                    <p className="mb-0 fw-medium">{selectedMember.role}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <p className="text-muted small mb-1">Status</p>
                    <p className="mb-0">{getStatusBadge(selectedMember.status)}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <p className="text-muted small mb-1">Join Date</p>
                    <p className="mb-0 fw-medium">{selectedMember.joinDate}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <p className="text-muted small mb-1">Phone</p>
                    <p className="mb-0 fw-medium">{selectedMember.phone}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <p className="text-muted small mb-1">Country</p>
                    <p className="mb-0 fw-medium">{selectedMember.country}</p>
                  </div>
                </div>
                
                {/* Display additional fields from the full data if available */}
                {selectedMember.fullData && (
                  <div className="row mb-3">
                    {selectedMember.title && (
                      <div className="col-md-6 mb-3">
                        <p className="text-muted small mb-1">Title</p>
                        <p className="mb-0 fw-medium">{selectedMember.title}</p>
                      </div>
                    )}
                    {selectedMember.participationType && (
                      <div className="col-md-6 mb-3">
                        <p className="text-muted small mb-1">Participation Type</p>
                        <p className="mb-0 fw-medium">{selectedMember.participationType}</p>
                      </div>
                    )}
                    {selectedMember.companyEmail && (
                      <div className="col-md-6 mb-3">
                        <p className="text-muted small mb-1">Company Email</p>
                        <p className="mb-0 fw-medium">{selectedMember.companyEmail}</p>
                      </div>
                    )}
                    {selectedMember.companyPhone && (
                      <div className="col-md-6 mb-3">
                        <p className="text-muted small mb-1">Company Phone</p>
                        <p className="mb-0 fw-medium">{selectedMember.companyPhone}</p>
                      </div>
                    )}
                    {selectedMember.companyWebsite && (
                      <div className="col-md-6 mb-3">
                        <p className="text-muted small mb-1">Company Website</p>
                        <p className="mb-0 fw-medium">
                          <a href={selectedMember.companyWebsite} target="_blank" rel="noopener noreferrer">
                            {selectedMember.companyWebsite}
                          </a>
                        </p>
                      </div>
                    )}
                    {selectedMember.companyAddress && (
                      <div className="col-md-6 mb-3">
                        <p className="text-muted small mb-1">Company Address</p>
                        <p className="mb-0 fw-medium">{selectedMember.companyAddress}</p>
                      </div>
                    )}
                    {selectedMember.companySector && (
                      <div className="col-md-6 mb-3">
                        <p className="text-muted small mb-1">Company Sector</p>
                        <p className="mb-0 fw-medium">{selectedMember.companySector}</p>
                      </div>
                    )}
                    {selectedMember.sponsorshipInterest && (
                      <div className="col-md-6 mb-3">
                        <p className="text-muted small mb-1">Sponsorship Interest</p>
                        <p className="mb-0 fw-medium">{selectedMember.sponsorshipInterest ? 'Yes' : 'No'}</p>
                      </div>
                    )}
                    {selectedMember.comments && (
                      <div className="col-12 mb-3">
                        <p className="text-muted small mb-1">Comments</p>
                        <p className="mb-0">{selectedMember.comments}</p>
                      </div>
                    )}
                    {selectedMember.b2bInterests && selectedMember.b2bInterests.length > 0 && (
                      <div className="col-12 mb-3">
                        <p className="text-muted small mb-1">B2B Interests</p>
                        <div className="d-flex flex-wrap gap-1">
                          {selectedMember.b2bInterests.map((interest, index) => (
                            <span key={index} className="badge bg-primary">{interest}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedMember.b2bDetails && (
                      <div className="col-12 mb-3">
                        <p className="text-muted small mb-1">B2B Details</p>
                        <p className="mb-0">{selectedMember.b2bDetails}</p>
                      </div>
                    )}
                    {selectedMember.companyProfile && (
                      <div className="col-12 mb-3">
                        <p className="text-muted small mb-1">Company Profile</p>
                        <p className="mb-0">{selectedMember.companyProfile}</p>
                      </div>
                    )}
                    {selectedMember.productsServices && (
                      <div className="col-12 mb-3">
                        <p className="text-muted small mb-1">Products & Services</p>
                        <p className="mb-0">{selectedMember.productsServices}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowViewModal(false)}>Close</button>
                <button type="button" className="btn btn-primary" onClick={() => {
                  setShowViewModal(false);
                  handleEditMember(selectedMember);
                }}>Edit Member</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Member</h5>
                <button type="button" className="btn-close" onClick={() => setShowAddMemberModal(false)}></button>
              </div>
              <div className="modal-body">
                <form>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="title" className="form-label">Title</label>
                      <input type="text" className="form-control" id="title" placeholder="Enter title" />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="fullName" className="form-label">Full Name</label>
                      <input type="text" className="form-control" id="fullName" placeholder="Enter full name" />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="email" className="form-label">Email Address</label>
                      <input type="email" className="form-control" id="email" placeholder="Enter email address" />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="phone" className="form-label">Phone Number</label>
                      <input type="tel" className="form-control" id="phone" placeholder="Enter phone number" />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="position" className="form-label">Position</label>
                      <input type="text" className="form-control" id="position" placeholder="Enter position" />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="organization" className="form-label">Organization</label>
                      <input type="text" className="form-control" id="organization" placeholder="Enter organization" />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="country" className="form-label">Country</label>
                      <input type="text" className="form-control" id="country" placeholder="Enter country" />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="participationType" className="form-label">Participation Type</label>
                      <select className="form-select" id="participationType">
                        <option value="">Select participation type</option>
                        <option value="attendee">Attendee</option>
                        <option value="exhibitor">Exhibitor</option>
                        <option value="sponsor">Sponsor</option>
                        <option value="speaker">Speaker</option>
                      </select>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="companyEmail" className="form-label">Company Email</label>
                      <input type="email" className="form-control" id="companyEmail" placeholder="Enter company email" />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="companyPhone" className="form-label">Company Phone</label>
                      <input type="tel" className="form-control" id="companyPhone" placeholder="Enter company phone" />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="companyWebsite" className="form-label">Company Website</label>
                      <input type="url" className="form-control" id="companyWebsite" placeholder="Enter company website" />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="companySector" className="form-label">Company Sector</label>
                      <input type="text" className="form-control" id="companySector" placeholder="Enter company sector" />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="companyAddress" className="form-label">Company Address</label>
                    <textarea className="form-control" id="companyAddress" rows="2" placeholder="Enter company address"></textarea>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="comments" className="form-label">Comments</label>
                    <textarea className="form-control" id="comments" rows="3" placeholder="Enter any additional comments"></textarea>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddMemberModal(false)}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={() => {
                  // Handle form submission
                  setShowAddMemberModal(false);
                }}>Add Member</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation */}
      <div className="d-md-none mobile-bottom-nav fixed-bottom bg-white shadow-lg">
        <div className="d-flex justify-content-around">
          <button className="btn btn-link text-dark">
            <i className="bi bi-house-door-fill fs-5"></i>
            <small className="d-block">Dashboard</small>
          </button>
          <button className="btn btn-link text-dark">
            <i className="bi bi-people-fill fs-5"></i>
            <small className="d-block">Members</small>
          </button>
          <button className="btn btn-link text-dark">
            <i className="bi bi-calendar-event fs-5"></i>
            <small className="d-block">Meetings</small>
          </button>
          <button className="btn btn-link text-dark">
            <i className="bi bi-gear fs-5"></i>
            <small className="d-block">Settings</small>
          </button>
        </div>
      </div>

      <style jsx>{`
        .app {
          display: flex;
          min-height: 100vh;
          position: relative;
        }
        
        .sidebar-wrapper {
          width: 250px;
          flex-shrink: 0;
          transition: transform 0.3s ease;
        }
        
        .main-content {
          flex-grow: 1;
          padding-bottom: 60px; /* Space for mobile bottom nav */
        }
        
        .mobile-header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
        }
        
        .mobile-bottom-nav {
          height: 60px;
          z-index: 1000;
        }
        
        .mobile-menu-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          z-index: 999;
        }
        
        @media (max-width: 767.98px) {
          .sidebar-wrapper {
            position: fixed;
            top: 0;
            left: 0;
            bottom: 0;
            z-index: 1000;
            transform: translateX(-100%);
          }
          
          .sidebar-wrapper.mobile-open {
            transform: translateX(0);
          }
          
          .main-content {
            margin-left: 0;
          }
        }
        
        /* Stats Card Styles */
        .stats-card {
          transition: all 0.3s ease;
        }
        
        .stats-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1) !important;
        }
        
        .stats-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
        }
        
        .stats-icon-primary {
          background: rgba(99, 102, 241, 0.1);
          color: #6366f1;
        }
        
        .stats-icon-info {
          background: rgba(23, 162, 184, 0.1);
          color: #17a2b8;
        }
        
        .stats-icon-success {
          background: rgba(40, 167, 69, 0.1);
          color: #28a745;
        }
        
        .stats-icon-danger {
          background: rgba(220, 53, 69, 0.1);
          color: #dc3545;
        }
        
        .stats-icon-warning {
          background: rgba(255, 193, 7, 0.1);
          color: #ffc107;
        }
        
        /* Table Styles */
        .table th {
          font-weight: 600;
          color: #495057;
          border-bottom: 1px solid #dee2e6;
        }
        
        .user-avatar {
          font-weight: 600;
        }
      `}</style>
    </div>
  )
}